// editor-view.js - small DRY helper over the shared CM6 building blocks.
//
// Not a CM5 facade: no CodeMirror-shaped instance is reconstructed here. This
// exposes plain functions (createYamlEditor, getDoc, setDoc, getSelectionText)
// over a real @codemirror/view EditorView, which every call site can import
// directly instead of duplicating the same three-line dispatch.

import { Annotation, EditorState, Prec } from '@codemirror/state';
import { EditorView, lineNumbers } from '@codemirror/view';
import { history } from '@codemirror/commands';
import { foldGutter, indentUnit, syntaxHighlighting } from '@codemirror/language';
import { dbtChartsCompletionExtensions, dbtChartsFormatExtensions, dbtChartsHighlightStyle, dbtChartsIndentKeymap, dbtChartsIndentService, dbtChartsKeymap, dbtChartsYamlLanguage } from 'codemirror-dbt-charts';

// Marks a transaction as a programmatic setDoc() so callers (editor.js's
// updateListener) can skip the debounced-preview path a typed edit triggers.
// CM6 has no `change.origin` string like CM5 did.
export const ProgrammaticChange = Annotation.define();

/**
 * Build the playground's YAML editor: the shared language + indent +
 * formatting + completion extensions, the shared VSCode-parity keymap
 * (completionKeymap ships separately, inside dbtChartsCompletionExtensions),
 * and line numbers/folding/wrapping/highlighting. `sqlBlocks` and `catalog`
 * are required - this module fetches nothing itself.
 *
 * Formatting here is the command and the paste re-base only. Format-on-save is
 * a Cloud preference: the playground has no accounts and writes no git.
 */
export function createYamlEditor({ parent, doc = '', sqlBlocks, catalog, onDocChange }) {
    return new EditorView({
        doc,
        parent,
        extensions: [
            lineNumbers(),
            foldGutter(),
            history(),
            syntaxHighlighting(dbtChartsHighlightStyle),
            indentUnit.of('  '),
            EditorState.tabSize.of(2),
            EditorView.lineWrapping,
            dbtChartsYamlLanguage(sqlBlocks),
            dbtChartsIndentService(),
            // Above defaultKeymap's Enter, below the completion package's own
            // blank-line Enter (Prec.highest).
            Prec.high(dbtChartsIndentKeymap()),
            dbtChartsFormatExtensions(),
            dbtChartsCompletionExtensions(catalog),
            dbtChartsKeymap(),
            EditorView.updateListener.of(onDocChange),
        ],
    });
}

export function getDoc(view) {
    return view.state.doc.toString();
}

export function setDoc(view, text) {
    view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: text },
        annotations: ProgrammaticChange.of(true),
    });
}

export function getSelectionText(view) {
    const { from, to } = view.state.selection.main;
    return view.state.sliceDoc(from, to);
}
